/**
 * The SCENERY REGISTRY — the seam that makes EVERY discrete office piece editable, not just the shared
 * furniture catalog. Each room module registers its own bespoke builders (the CEO uplighter, the arcade
 * cabinet, the conference table, the fridge, …) here under a stable kind. The level editor then:
 *   • CAPTURES them into {@link DecorItem} data (via the `place(ctx, piece, x, y, kind)` tag), and
 *   • RE-RENDERS / ADDS them from this registry — so they move/delete/add like any other furniture.
 *
 * Bespoke builders take a {@link RoomContext}; the registry calls them with a minimal SYNTHETIC context
 * (theme art + colour helpers + the furniture catalog, anchored at the origin) since the build is
 * local-space and the editor positions the piece itself. Painted backdrop (floor washes, patterns) is NOT
 * registered — it stays room scenery.
 */
import { Container } from 'pixi.js';
import type { WorldZone } from '@duck/contracts';
import { darken, lighten, mix, type ThemeArt } from './theme-art';
import * as furniture from './furniture';
import type { RoomContext } from './rooms';

interface SceneryEntry {
  build: (art: ThemeArt) => Container;
  label: string;
  icon: string;
}

const SCENERY = new Map<string, SceneryEntry>();

const DUMMY_ZONE: WorldZone = { id: '', name: '', x: 0, y: 0, width: 0, height: 0, isolated: false };

/** A minimal {@link RoomContext} so a room-local builder can be invoked standalone (origin-anchored). */
export function synthCtx(art: ThemeArt): RoomContext {
  return { container: new Container(), zone: DUMMY_ZONE, art, f: furniture, mix, lighten, darken, cx: 0, cy: 0 };
}

/**
 * Register a bespoke piece as an editable scenery kind (called at room-module load). `build` returns the
 * piece in local space; `label`/`icon` feed the editor palette so the kind is also ADD-able.
 */
export function registerScenery(
  kind: string,
  build: (art: ThemeArt) => Container,
  label: string,
  icon: string,
): void {
  SCENERY.set(kind, { build, label, icon });
}

/** Build a registered scenery piece (or null if the kind is unknown). */
export function buildScenery(kind: string, art: ThemeArt): Container | null {
  const entry = SCENERY.get(kind);
  return entry ? entry.build(art) : null;
}

/** Is `kind` a registered scenery kind? */
export function isScenery(kind: string): boolean {
  return SCENERY.has(kind);
}

/** The registered scenery kinds for the editor palette (dual-coded icon + label). */
export function sceneryCatalog(): ReadonlyArray<{ kind: string; label: string; icon: string }> {
  return [...SCENERY].map(([kind, e]) => ({ kind, label: e.label, icon: e.icon }));
}
